import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Eye, EyeOff, AlertCircle, Loader2 } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Where to send the user after a successful sign-in.
  //   • ProtectedRoute stores the URL the user tried to load in
  //     ``location.state.from`` (a react-router location: pathname+search+hash).
  //   • On refresh of e.g. ``/projects/xyz``, we want to land back there —
  //     NOT on ``/`` (which would look like "the app forgot my page").
  //   • ``/login`` and ``/signup`` are never valid targets — fall back to ``/``.
  const from = location.state?.from;
  const nextPath = from
    ? `${from.pathname || '/'}${from.search || ''}${from.hash || ''}`
    : '/';
  const safeNext = nextPath === '/login' || nextPath === '/signup' ? '/' : nextPath;

  // If the user is already signed in (e.g. still-valid session after a
  // full-page reload landed them here), forward them to ``safeNext``
  // instead of making them log in again just to be sent to the dashboard.
  useEffect(() => {
    if (!authLoading && user) {
      navigate(safeNext, { replace: true });
    }
  }, [authLoading, user, navigate, safeNext]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error } = await signIn(email, password);

      if (!error && data?.session) {
        navigate(safeNext, { replace: true });
        return;
      }

      if (error) {
        if (error.message.includes('Email not confirmed')) {
          setError('Please verify your email before signing in.');
        } else if (error.message.includes('Invalid')) {
          setError('Invalid email or password.');
        } else {
          setError(error.message);
        }
      } else {
        setError('Invalid email or password.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 relative overflow-hidden">
      {/* Background video - looped, muted, autoplay. Two sources so every
          modern browser can decode it:
            • H.264/MP4   — universally supported in Google Chrome, Safari,
              Edge, Firefox (with system codecs), iOS.
            • VP9/WebM    — fallback for open-source Chromium builds (no
              proprietary H.264 license), privacy browsers, and headless
              automation environments.
          The poster keeps the original BIM image as the first paint and as
          a graceful fallback if a browser blocks autoplay (iOS Low-Power
          Mode, prefers-reduced-motion=reduce, etc.). ``muted`` + ``playsInline``
          are required for autoplay in every modern browser. */}
      <div className="absolute inset-0 z-0">
        <video
          poster="https://customer-assets.emergentagent.com/job_ifc-validation-tool/artifacts/flnqs8gb_Gemini_Generated_Image_at5pchat5pchat5p.png"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="w-full h-full object-cover opacity-70"
          data-testid="login-bg-video"
          aria-hidden="true"
        >
          <source src="/login-bg.webm" type="video/webm" />
          <source src="/login-bg.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/50 to-background/70"></div>
      </div>
      <div className="w-full max-w-md relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2">
            <span className="text-primary">AHA</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            Autonomous AI-Enabled Handover Agent
          </p>
        </div>

        {/* Login Card */}
        <div className="card-elevated p-8 bg-card/95 backdrop-blur-sm">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-foreground">Sign In</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Enter your credentials to access the platform
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6" data-testid="login-form-error">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  disabled={loading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="login-password-visibility-button"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Don't have an account? </span>
            <Link to="/signup" className="text-primary hover:underline font-medium" data-testid="login-signup-link">
              Sign up
            </Link>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-8">
          Professional BIM Management Platform for AECO Industry
        </p>
      </div>
    </div>
  );
}